/**
 * Edit Patient Details voice actions — set_edit_patient_field /
 * update_patient / cancel_edit_patient.
 *
 * Mounted from PatientEditDrawer, which renders ONLY while the Edit
 * Patient popup is open — so these actions are naturally scoped (off the
 * popup they're unregistered and the agent gets unknown_action). Drives
 * the SAME react-hook-form (formControlObj) and the SAME update/cancel
 * handlers the manual Update/Cancel buttons use. open_edit_patient (to
 * open the popup) is registered separately by patientInfoWithAvatar.
 *
 * Field-fill reuses applyPatientField (shared with Create Patient). The
 * name accumulator is seeded from the current patient name so partial
 * updates (e.g. "change last name") preserve the other name parts.
 *
 * Scope "recording_screen".
 */

import { useRef } from "react";

import { applyPatientField } from "./applyPatientField";
import { useCopilotAction, copilotBlocked } from "./actionRegistry";

const SCOPE = "recording_screen";

const SET_FIELD_DESCRIPTOR = Object.freeze({
  name: "set_edit_patient_field",
  description: "Implemented by noki-fe (PatientEditDrawer form setValue).",
  classification: "WRITE_SAFE",
  confirmStyle: "none",
  parameters: [
    {
      name: "field",
      type: "string",
      description: "first_name|middle_name|last_name|date_of_birth|gender|phone|email",
      required: true,
    },
    { name: "value", type: "string", description: "The new value.", required: true },
  ],
});

const UPDATE_DESCRIPTOR = Object.freeze({
  name: "update_patient",
  description: "Implemented by noki-fe (PatientEditDrawer.handleOnSavePatient via handleSubmit).",
  classification: "WRITE_SAFE",
  confirmStyle: "none",
  parameters: [],
});

const CANCEL_DESCRIPTOR = Object.freeze({
  name: "cancel_edit_patient",
  description: "Implemented by noki-fe (PatientEditDrawer close).",
  classification: "READ",
  parameters: [],
});

export function useCopilotEditPatientActions({
  setValue,
  clearErrors,
  initialName,
  submit,
  cancel,
}) {
  // Seed the name accumulator from the patient's existing FHIR name so a
  // partial update ("change first name") preserves middle/last.
  const nameRef = useRef({
    first: initialName?.given?.[0] || "",
    middle: initialName?.given?.[1] || "",
    last: initialName?.family || "",
  });
  // In-flight guard: the voice path bypasses the drawer button's disabled
  // state, so a rapid double "save" could otherwise submit twice.
  const updatingRef = useRef(false);

  useCopilotAction(
    SET_FIELD_DESCRIPTOR,
    async (args) => {
      if (typeof setValue !== "function") {
        throw new Error("host_exception: edit form setter not wired.");
      }
      const result = applyPatientField({
        field: args?.field,
        value: args?.value,
        setValue,
        clearErrors,
        nameRef,
      });
      // eslint-disable-next-line no-console
      console.info("[copilot] set_edit_patient_field", { field: result.field });
      return result;
    },
    { scope: SCOPE },
  );

  useCopilotAction(
    UPDATE_DESCRIPTOR,
    async () => {
      if (typeof submit !== "function") {
        throw new Error("host_exception: update handler not wired.");
      }
      if (updatingRef.current) {
        return copilotBlocked(
          "busy",
          "A patient update is already in progress. Wait for it to finish.",
        );
      }
      updatingRef.current = true;
      try {
        // Await if the submit returns a promise (react-hook-form handleSubmit);
        // harmless if not. Guards the double-submit window either way.
        await submit();
        return { submitted: true, next_step: "Validating and updating the patient; tell the user their details are being updated." };
      } finally {
        updatingRef.current = false;
      }
    },
    { scope: SCOPE },
  );

  useCopilotAction(
    CANCEL_DESCRIPTOR,
    async () => {
      if (typeof cancel !== "function") {
        throw new Error("host_exception: cancel handler not wired.");
      }
      cancel();
      return { cancelled: true };
    },
    { scope: SCOPE },
  );
}
